import { useContext, useMemo } from 'react';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { GrowthbookReadyContext } from 'services/growthbook/GrowthbookWrapper';
import { MicrositeContext, OffersContext } from 'context';
import { useQual } from 'hooks/useQual';
import { useIsZipOnlyAddress } from 'hooks/useIsZipOnly';
import { getTopCrossSellProvider } from 'config/providers';
import {
  Address,
  CheckoutType,
  CrossSellInput,
  ShopType,
  SingleProviderConfig,
} from 'types';

export interface ProviderRedirect {
  /**
   * True when this microsite should send the user straight to the provider
   * instead of showing them /plans. Both the redirect modal and the /plans
   * offer suppression key off this single value, so the two can never
   * disagree and leave the user staring at an empty page.
   */
  redirectEnabled: boolean;
  redirectUrl?: string;
  /**
   * True when the redirect is firing because the primary query came back with
   * no offers, on a microsite that opted in. The modals that would otherwise
   * greet an unserviceable address key off this to stand down.
   */
  redirectOnUnavailable: boolean;
  /**
   * True while this microsite is in redirect mode but its Growthbook gate has
   * not resolved yet. Callers that hide content on redirect must keep waiting
   * rather than treating an undecided gate as "no redirect" — otherwise the
   * plans page paints and is then yanked away.
   */
  redirectPending: boolean;
}

/**
 * Resolves whether a PROVIDER_REDIRECT microsite can actually redirect, and
 * to where. Three things have to line up: the microsite is a single-provider
 * shop in PROVIDER_REDIRECT mode, its Growthbook gate (if it has one) is on,
 * and a url exists.
 *
 * The gate fails closed — an unset flag, a still-loading client, or a
 * Growthbook outage all fall back to /plans rather than redirecting on a
 * guess, mirroring `useLinkoutOffersEnabled`.
 */
export function useProviderRedirect(
  updatedCheckoutType: CheckoutType
): ProviderRedirect {
  const { primaryData, secondaryData } = useContext(OffersContext);
  const micrositeConfig = useContext(MicrositeContext);
  const decodedQuery = useQual();
  const growthbook = useGrowthBook();
  const isGrowthbookReady = useContext(GrowthbookReadyContext);
  const isZipOnly = useIsZipOnlyAddress();

  const {
    shopType,
    mainProvider,
    getProviderRedirectUrl,
    providerRedirectFeatureFlag,
    redirectWhenUnavailable,
    crossSellProviders,
  } = (micrositeConfig ?? {}) as SingleProviderConfig;

  // Named rather than inlined into one boolean chain: each of these is a
  // separate reason to hold the redirect, and they are asserted separately.
  const isRedirectShop =
    shopType === ShopType.SINGLE_PROVIDER &&
    updatedCheckoutType === CheckoutType.PROVIDER_REDIRECT &&
    typeof getProviderRedirectUrl === 'function';

  const gateUndecided =
    Boolean(providerRedirectFeatureFlag) && (!isGrowthbookReady || !growthbook);

  const gateOpen = !providerRedirectFeatureFlag
    ? true
    : !gateUndecided && Boolean(growthbook?.isOn(providerRedirectFeatureFlag));

  const offersSettled = !primaryData?.loading;

  const eligible = isRedirectShop && offersSettled && gateOpen;

  const redirectUrl = useMemo(() => {
    if (!eligible) return undefined;

    const address = {
      street: decodedQuery.street,
      unit: decodedQuery.unit,
      city: decodedQuery.city,
      state: decodedQuery.state,
      postalCode: decodedQuery.zip,
      line2Type: decodedQuery.line2Type,
    } as Address;

    return getProviderRedirectUrl({
      // The microsite's mainProvider, not availableProviders[0]: this field is
      // typed as our ProviderConfig, and s12y hands back a
      // ServiceabilityProvider that merely shares a `code` field with it.
      providerConfig: mainProvider,
      serviceabilityOffers: primaryData?.availableOffers,
      dcFwsTid: primaryData?.providerSummaries?.[0]?.falconIdentifier,
      uuid: decodedQuery.uuid,
      address,
    } as CrossSellInput);
  }, [
    eligible,
    decodedQuery,
    primaryData,
    mainProvider,
    getProviderRedirectUrl,
  ]);

  const providerSummaries = primaryData?.providerSummaries;
  const primarySummary = providerSummaries?.[0];
  const hasSuggestions = Boolean(primarySummary?.addressSuggestions?.length);

  // An 'unavailable' answer is final, so it redirects even with suggestions.
  // Anything else with suggestions is a typo the address flow can still fix.
  const unavailableResponse =
    !primaryData?.availableOffers?.length &&
    !providerSummaries?.some(
      (summary) => summary.availability === 'available'
    ) &&
    (primarySummary?.availability === 'unavailable' || !hasSuggestions);

  // The cross-sell modal owns this address when it has a provider to offer, so
  // the redirect waits for the secondary query and only takes what it leaves.
  const crossSellWorks =
    secondaryData?.availableOffers?.length > 0 &&
    getTopCrossSellProvider(
      crossSellProviders,
      secondaryData.availableProviders
    ) !== undefined;

  return {
    redirectEnabled: eligible && redirectUrl !== undefined,
    redirectUrl,
    redirectOnUnavailable:
      Boolean(redirectWhenUnavailable) &&
      eligible &&
      !isZipOnly &&
      unavailableResponse &&
      !secondaryData?.loading &&
      !crossSellWorks &&
      redirectUrl !== undefined,
    redirectPending: isRedirectShop && gateUndecided,
  };
}
